import { getAddonPrice } from "./quiz-results-addons.js";
import { getQuizResultsTexts } from "./quiz-results-texts.js";

/**
 * @param {string | number} value
 * @returns {number}
 */
function roundToTwo(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {string | number} value
 * @returns {string}
 */
function formatPrice(value) {
  const num = roundToTwo(value);
  return `$${num % 1 === 0 ? num : num.toFixed(2)}`;
}

/**
 * @param {Element | null} rootElement
 * @param {() => void} onCheckout
 * @returns {() => void}
 */
export function bindStickyBarBlock(rootElement, onCheckout) {
  if (!rootElement || typeof onCheckout !== "function") return () => {};

  const button = rootElement.querySelector("[data-sticky-checkout-button]");
  if (!button) return () => {};

  const handleClick = () => {
    onCheckout();
  };

  button.addEventListener("click", handleClick);
  return () => {
    button.removeEventListener("click", handleClick);
  };
}

/**
 * @param {any} regimenProduct
 * @param {any[]} selectedAddons
 * @param {any} discount
 * @param {boolean} isOtpOn
 * @returns {{ fullTotal: number, discountedTotal: number }}
 */
export function getStickyTotals(regimenProduct, selectedAddons, discount, isOtpOn) {
  const addons = Array.isArray(selectedAddons) ? selectedAddons : [];
  const regimenFullPrice = Number(regimenProduct?.variant?.price || 0);
  const regimenDiscountValue = Number(discount?.value || 0);
  const regimenDiscountedPrice = regimenFullPrice * (1 - regimenDiscountValue / 100);

  const addonsFullTotal = addons.reduce(
    (sum, addon) => sum + Number(addon?.variant?.price || 0),
    0,
  );
  const addonsDiscountedTotal = addons.reduce(
    (sum, addon) => sum + getAddonPrice(addon?.variant, isOtpOn),
    0,
  );

  return {
    fullTotal: roundToTwo(regimenFullPrice + addonsFullTotal),
    discountedTotal: roundToTwo(regimenDiscountedPrice + addonsDiscountedTotal),
  };
}

/**
 * @param {Element | null} rootElement
 * @param {{ fullTotal: number, discountedTotal: number, isLoading: boolean }} state
 */
export function renderStickyBarBlock(rootElement, state) {
  if (!rootElement) return;

  const discountedElement = rootElement.querySelector("[data-sticky-total-discounted]");
  const fullElement = rootElement.querySelector("[data-sticky-total-full]");
  const button = rootElement.querySelector("[data-sticky-checkout-button]");

  const fullTotal = Number(state?.fullTotal || 0);
  const discountedTotal = Number(state?.discountedTotal || 0);
  const isLoading = Boolean(state?.isLoading);

  if (discountedElement) {
    discountedElement.textContent = formatPrice(discountedTotal);
  }

  if (fullElement) {
    const shouldShowFullTotal = discountedTotal !== fullTotal;
    fullElement.textContent = formatPrice(fullTotal);
    fullElement.classList.toggle("is-hidden", !shouldShowFullTotal);
  }

  if (button) {
    if (isLoading) {
      button.setAttribute("disabled", "disabled");
      button.setAttribute("aria-busy", "true");
      button.classList.add("loading");
    } else {
      button.removeAttribute("disabled");
      button.setAttribute("aria-busy", "false");
      button.classList.remove("loading");
    }
  }

}

/**
 * @param {any} sellingPlan
 * @returns {boolean}
 */
function isSellingPlanMatching(sellingPlan) {
  const frequency = Number(sellingPlan?.frequency || 0);
  const unit = String(sellingPlan?.unit || "");
  return (
    (frequency === 90 && unit === "day") ||
    (frequency === 12 && unit === "week") ||
    (frequency === 3 && unit === "month")
  );
}

/**
 * @param {{
 *   regimenProduct: any,
 *   selectedAddons: any[],
 *   discount: any,
 *   isOtpOn: boolean,
 *   email: string
 * }} input
 * @returns {any[]}
 */
function getCheckoutItems(input) {
  const texts = getQuizResultsTexts();
  const { regimenProduct, selectedAddons, discount, isOtpOn, email } = input;
  const addons = Array.isArray(selectedAddons) ? selectedAddons : [];
  const items = /** @type {any[]} */ ([]);

  const sellingPlan = (regimenProduct?.selling_plans || []).find((/** @type {any} */ plan) =>
    isSellingPlanMatching(plan),
  );
  const regimenItem = {
    id: Number(regimenProduct?.variant?.id),
    quantity: 1,
    selling_plan: !isOtpOn && sellingPlan ? Number(sellingPlan.selling_plan_id) : undefined,
    properties: {
      _g_is_trial: true,
      _g_trial_type: texts.stickyBar.trialType,
      _g_url: window.location.href,
      _g_discount: discount || null,
      _g_compare_at_price: Number(regimenProduct?.variant?.price || 0),
      _g_frequency: isOtpOn
        ? texts.stickyBar.frequencyOneTime
        : texts.stickyBar.frequencyRefill,
      _g_email: email || "",
      _g_set_type: texts.stickyBar.setType,
    },
  };
  items.push(regimenItem);

  addons.forEach((addon) => {
    const compareAtPrice = Number(addon?.variant?.price || 0);
    const addonPrice = getAddonPrice(addon?.variant, isOtpOn);
    const addonItem = /** @type {any} */ ({
      id: Number(addon?.variant?.id),
      quantity: 1,
      properties: {
        _g_trial_type: texts.stickyBar.trialType,
        _g_url: window.location.href,
        _g_frequency: texts.stickyBar.frequencyOneTime,
      },
    });

    if (addonPrice < compareAtPrice) {
      addonItem.properties._g_addon_price = addonPrice.toFixed(2);
      addonItem.properties._g_compare_at_price = compareAtPrice;
    }

    items.push(addonItem);
  });

  return items.filter((/** @type {any} */ item) => Number.isFinite(item.id));
}

/**
 * @returns {Promise<any[]>}
 */
async function getTrialCartItems() {
  const texts = getQuizResultsTexts();
  const response = await fetch("/cart.js", {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error(texts.stickyBar.cartFetchError);
  }

  const cart = await response.json();
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.filter(
    (/** @type {any} */ item) =>
      item?.properties?._g_trial_type === texts.stickyBar.trialType,
  );
}

/**
 * @param {Record<string, number>} updates
 * @returns {Promise<void>}
 */
async function updateCartItems(updates) {
  const texts = getQuizResultsTexts();
  if (!Object.keys(updates).length) return;

  const response = await fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify({ updates }),
  });

  if (!response.ok) {
    throw new Error(texts.stickyBar.cartUpdateError);
  }
}

/**
 * @param {any[]} items
 * @returns {Promise<void>}
 */
async function addItemsToCart(items) {
  const texts = getQuizResultsTexts();
  if (!items.length) return;

  const response = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    throw new Error(texts.stickyBar.cartAddError);
  }
}

/**
 * @param {{
 *   regimenProduct: any,
 *   selectedAddons: any[],
 *   discount: any,
 *   isOtpOn: boolean,
 *   email: string
 * }} input
 * @returns {Promise<void>}
 */
export async function runStickyCheckoutFlow(input) {
	const { regimenProduct, selectedAddons, discount, isOtpOn, email } = input;
	const trialItems = await getTrialCartItems();
  const updates = /** @type {Record<string, number>} */ ({});
  trialItems.forEach((item) => {
    if (item?.key) {
      updates[item.key] = 0;
    }
  });

  const itemsToAdd = getCheckoutItems(input);
  await updateCartItems(updates);
  await addItemsToCart(itemsToAdd);

  const discountCode = String(discount?.code || "").trim();
  window.location.href = discountCode
    ? `/checkout?discount=${encodeURIComponent(discountCode)}`
    : "/checkout";
}
